import { useCallback } from 'react'
import { CancelToken } from 'axios'
import { ApiAccess } from 'features/session/types'
import {
  ApiGetWithToken,
  ApiGetFileWithToken,
  ApiPostFileWithToken,
	ApiPostWithToken,
	ApiPatchWithToken,
	ApiDeleteWithToken,
	useApiFetch,
	useApiPost,
	FileResponse
} from 'shared/api'
import {
  manufacturerApi,
  manufacturerApiBy
} from 'interface/endpoints'
import {
	ManufacturerFormData,
  ManufacturerResult,
  ManufacturerResultList
} from './types'
import { safeParseDate } from 'shared/util'

export * from './types'

export const useListManufacturer = (
) => {
	return useApiFetch(
		useCallback(
			(apiAccess, cancelToken) => {
				return ApiGetWithToken<ManufacturerResultList>(
					apiAccess,
					manufacturerApi('/list'),
					cancelToken
				)
			},
			[]
		)
	)
}

export const useGetManufacturer = (
  manufacturerId?: number | null
) => {
	return useApiFetch(
		useCallback(
			(apiAccess, cancelToken) => {
				if (!manufacturerId) return null
				return ApiGetWithToken<ManufacturerResult>(
					apiAccess,
					manufacturerApiBy(manufacturerId),
					cancelToken
				)
			},
			[manufacturerId]
		)
	)
}

const postManufacturerLogo = (
	apiAccess: ApiAccess,
	manufacturerId: number,
	file: File, 
	cancelToken?: CancelToken,
	webSocketId?: string
) => {
	const formData = new FormData()
	formData.append('file', file)
	return ApiPostFileWithToken(
		apiAccess,
		manufacturerApiBy(manufacturerId, '/logo'),
		formData,
		cancelToken,
		webSocketId
	)
}

export const usePostManufacturer = (
  callback?: {
    onPosted?: (result: ManufacturerResult, logo?: FileResponse) => void
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onError?: (reason: any) => void
  }
) => useApiPost<{ form: ManufacturerFormData, logo?: File }, ManufacturerResult>(
	useCallback(
		(apiAccess, cancelToken, { form }, webSocketId) => {
			return ApiPostWithToken(
				apiAccess,
        manufacturerApi('/create'),
				form,
				cancelToken,
				webSocketId
			)
		},
		[]
	),
	useCallback(
		async (result, { logo }, apiAccess, cancelToken) => {
			if (logo) {
				const formData = new FormData()
				formData.append('file', logo)
				const logoResult = await postManufacturerLogo(apiAccess, result.manufacturerId, logo, cancelToken)
				if (callback?.onPosted) callback.onPosted(result, logoResult)
			} else {
				if (callback?.onPosted) callback.onPosted(result)
			}
			return result
		},
		[callback]
	),
	useCallback(
		(reason) => {
      if (callback?.onError) callback?.onError(reason)
		},
		[callback]
	)
)

export const useEditManufacturer = (
  manufacturerId: number, 
	callback?: {
    onEditted?: (result: ManufacturerResult, logo?: FileResponse) => void
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onError?: (reason: any) => void
  }
) => useApiPost<{ form: ManufacturerFormData, logo?: File }, ManufacturerResult>(
	useCallback(
		(apiAccess, cancelToken, { form }, webSocketId) => {
			return ApiPatchWithToken(
				apiAccess,
        manufacturerApiBy(manufacturerId),
				form,
				cancelToken,
				webSocketId
			)
		},
		[manufacturerId]
	),
	useCallback(
		async (result, { logo: file }, apiAccess, cancelToken) => {
			if (file) {
				const formData = new FormData()
				formData.append('file', file)
				const logo = await postManufacturerLogo(apiAccess, result.manufacturerId, file, cancelToken)
				if (callback?.onEditted) callback.onEditted(result, logo)
			} else {
				if (callback?.onEditted) callback.onEditted(result)
			}
			return result
		},
		[callback]
	),
	useCallback(
		(reason) => {
      if (callback?.onError) callback?.onError(reason)
		},
		[callback]
	)
)

export const useDeleteManufacturer = (
  callback?: {
    onDeleted?: (manufacturerId: number) => void
		onExistSearch?: () => void
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onError?: (reason: any) => void
  }
) => useApiPost<{ manufacturerId: number, checkExistSearch: boolean }, number>(
	useCallback(
		(apiAccess, cancelToken, { manufacturerId, checkExistSearch }, webSocketId) => {
			return ApiDeleteWithToken(
				apiAccess,
        manufacturerApiBy(manufacturerId, undefined, {
					checkExistSearch: checkExistSearch ? '1' : undefined
				}),
				cancelToken,
				webSocketId
			)
		},
		[]
	),
	useCallback(
		(result) => {
			if (callback?.onDeleted) callback.onDeleted(result)
			return result
		},
		[callback]
	),
	useCallback(
		(reason, { checkExistSearch }) => {
			// todo: reasonからリトライするかどうかを判定
			if (checkExistSearch && callback?.onExistSearch) {
				callback.onExistSearch()
				return
			}
			if (callback?.onError) callback?.onError(reason)
		},
		[callback]
	)
)

export const useGetManufacturerLogo = (
	manufacturerId?: number | null,
	time?: Date | string | null
) => {
	return useApiFetch(
		useCallback(
			(apiAccess, cancelToken) => {
				if (!manufacturerId) return null
				return ApiGetFileWithToken(
					apiAccess,
					manufacturerApiBy(manufacturerId, '/logo', {
						t: safeParseDate(time)?.getTime() ?? undefined
					}),
					cancelToken
				)
			},
			[manufacturerId, time]
		)
	)
}